import dayjs, { PluginFunc } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { nbMoment } from 'locales/nbMoment';
import { nnMoment } from 'locales/nnMoment';

/**
 * dayjs, configured once: import it from here, not from 'dayjs', so that every caller has the
 * plugins and the locales below whatever loaded first. It replaced moment in #228.
 *
 * - relativeTime: `.locale('nb').to(date, true)`, the "due in 3 days" labels.
 * - customParseFormat: `dayjs(value, 'DD/MM-YYYY')`.
 * - utc and timezone: `toAtom` in utils/dateFormatter.
 *
 * The locales are this app's own: months, short months and relative time. App makes the user's
 * language the global one at start-up (#229); until then every user got English abbreviations
 * from `format('DD. MMM. YYYY')`, because the locales had no short months.
 */
/**
 * `.locale(x)` with an x that is not there (null, undefined, '') keeps the date, as moment did.
 * dayjs returns the locale's name instead, and the `.fromNow()` or `.to()` after it throws. The
 * locales these calls read are set before the app renders, so this is a guard, not a path.
 */
const keepDateWithoutLocale: PluginFunc = (_options, Dayjs) => {
  // Taken off the prototype to be called with its own `this` below, through apply.
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { locale } = Dayjs.prototype;

  Dayjs.prototype.locale = function withLocale(this: dayjs.Dayjs, ...args: Array<unknown>) {
    if (args.length > 0 && !args[0]) {
      return this.clone();
    }
    return (locale as (...values: Array<unknown>) => dayjs.Dayjs | string).apply(this, args);
  } as typeof locale;
};

dayjs.extend(keepDateWithoutLocale);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * The locale files were written for moment, which fills a singular unit's %d ('%d minutt') with
 * 1. dayjs fills it with the count of the unit below (50 seconds, "50 minutt"), so the singular
 * units get moment's 1 here.
 */
const SINGULAR_UNITS = ['m', 'h', 'd', 'M', 'y'] as const;

const fromMomentLocale = (name: string, locale: typeof nbMoment): ILocale => {
  const relative: Record<string, string> = { ...locale.relativeTime };
  SINGULAR_UNITS.forEach((unit) => { relative[unit] = relative[unit].replace('%d', '1'); });

  return { name, months: locale.months, monthsShort: locale.monthsShort, relativeTime: relative } as ILocale;
};

dayjs.locale(fromMomentLocale('nb', nbMoment), undefined, true);
dayjs.locale(fromMomentLocale('nn', nnMoment), undefined, true);

export { dayjs };
export type { Dayjs } from 'dayjs';
